// Lib
import React from 'react';
import PropTypes from 'prop-types';
import AnimateHeight from 'react-animate-height';
import classNames from 'classnames';

// Hooks
import useUpdateEffect from '../../../utils/react/useUpdateEffect';
import usePrevious from '../../../utils/react/usePrevious';

// Constants
import { TOGGLE_BUTTON_TRANSITION_LENGTH } from './ToggleButton';

// Styles
import './ToggleContents.scss';

export const TOGGLE_TRANSITION_DURATION = TOGGLE_BUTTON_TRANSITION_LENGTH - 32;

/**
 * Animates the height of toggled in contents, and only renders the contents while "active"
 * is true, or it's transitioning out.
 */
const ToggleContents = (props) => {
    const {
        active,
        children,
        disabled,
        loading,
        forcedHeight = 'auto',
        duration = TOGGLE_TRANSITION_DURATION,
        easing = 'ease-out',
    } = props;

    const [renderContents, setRenderContents] = React.useState(active);
    const [height, setHeight] = React.useState(active ? forcedHeight : 0);
    // When the contents is being hidden, we don't want to show child updates, so instead
    // return the last rendered children
    const prevChildren = usePrevious(children);
    const [renderedChildren, setRenderedChildren] = React.useState(null);

    const timeoutRef = React.useRef();

    useUpdateEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        // Need to hide the contents
        if (!active) {
            // Render the previously rendered children when closing the toggled contents
            setRenderedChildren(prevChildren);
            setHeight(0);

            timeoutRef.current = setTimeout(() => setRenderContents(false), duration + 20);
            return;
        }

        // Otherwise, render the contents then animate the height
        // Clear any previously rendered children when showing the toggled contents
        setRenderedChildren(null);
        setRenderContents(active);

        requestAnimationFrame(() => setHeight(forcedHeight));
    }, [active]);

    const classes = classNames('ToggleContents', { disabled, loading });

    return (
        <AnimateHeight className={classes} duration={duration} height={height} easing={easing} animateOpacity>
            {renderedChildren || (renderContents && children)}
        </AnimateHeight>
    );
};

ToggleContents.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,

    active: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,

    forcedHeight: PropTypes.string,
    // Transition
    duration: PropTypes.number,
    easing: PropTypes.string,
};

export default ToggleContents;
